import { instance } from '../api/api';

// Partner API credential management: /api/autoparks/:autoparkID/partner/*
// Authorization is server-enforced (admin → any park, autopark_employee → own parks only).

export interface IPartnerClient {
  id: string;
  client_id: string;
  name: string;
  created_at: string; // RFC3339
  created_by: string | null; // profile id
  revoked_at: string | null; // RFC3339, null while live
}

// The only response that ever carries the secret. It must be shown once and never persisted or logged.
export interface IPartnerClientCreated extends IPartnerClient {
  client_secret: string;
}

export interface IPartnerToken {
  id: string;
  client_id: string; // public epl_… id of the issuing credential
  issued_at: string;
  expires_at: string;
  last_used_at: string | null;
}

export type PartnerAuditAction =
  | 'credential.created'
  | 'credential.revoked'
  | 'token.issued'
  | 'token.revoked'
  | 'credentials.access';

export type PartnerAuditOutcome = 'success' | 'denied' | 'error';

export interface IPartnerAuditEvent {
  id: string;
  occurred_at: string;
  actor_profile_id: string | null;
  actor_client_id: string | null;
  action: PartnerAuditAction | string;
  outcome: PartnerAuditOutcome | string;
  target_type: string;
  target_id: string;
  ip: string;
  request_id: string;
  detail: Record<string, unknown>;
}

// These endpoints render errors as {"errors":[{"message":"…"}]}, unlike the rest of the API
// (which api.ts already unwraps via `error`/`message`).
function errorMessage(error: any, fallback: string): string {
  const data = error?.response?.data;
  return data?.errors?.[0]?.message || data?.message || data?.error || fallback;
}

const base = (autoparkId: string) => `/autoparks/${autoparkId}/partner`;

export const PartnerService = {
  async getClients(autoparkId: string): Promise<IPartnerClient[]> {
    try {
      const { data } = await instance.get<IPartnerClient[]>(`${base(autoparkId)}/clients`);
      return data ?? [];
    } catch (error: any) {
      throw new Error(errorMessage(error, 'Не удалось получить список учётных данных'));
    }
  },

  async createClient(autoparkId: string, name: string): Promise<IPartnerClientCreated> {
    try {
      const { data } = await instance.post<IPartnerClientCreated>(`${base(autoparkId)}/clients`, { name });
      return data;
    } catch (error: any) {
      throw new Error(errorMessage(error, 'Не удалось создать учётные данные'));
    }
  },

  async revokeClient(autoparkId: string, clientId: string): Promise<void> {
    try {
      await instance.delete(`${base(autoparkId)}/clients/${clientId}`);
    } catch (error: any) {
      throw new Error(errorMessage(error, 'Не удалось отозвать учётные данные'));
    }
  },

  async getTokens(autoparkId: string): Promise<IPartnerToken[]> {
    try {
      const { data } = await instance.get<IPartnerToken[]>(`${base(autoparkId)}/tokens`);
      return data ?? [];
    } catch (error: any) {
      throw new Error(errorMessage(error, 'Не удалось получить список токенов'));
    }
  },

  async revokeToken(autoparkId: string, tokenId: string): Promise<void> {
    try {
      await instance.delete(`${base(autoparkId)}/tokens/${tokenId}`);
    } catch (error: any) {
      throw new Error(errorMessage(error, 'Не удалось отозвать токен'));
    }
  },

  async getAudit(autoparkId: string, limit: number = 50): Promise<IPartnerAuditEvent[]> {
    try {
      const { data } = await instance.get<IPartnerAuditEvent[]>(`${base(autoparkId)}/audit`, { params: { limit } });
      return data ?? [];
    } catch (error: any) {
      throw new Error(errorMessage(error, 'Не удалось получить историю'));
    }
  },
};
